import {
  BoundCheckboxField,
  BoundNumberField,
  BoundSelectField,
  BoundTextField,
  Css,
  FormLines,
} from "@homebound/beam";
import { ObjectConfig, required, useFormState } from "@homebound/form-state";
import { SaveCostCodeInput } from "src/generated/graphqlTypes";
import {
  useCostCodeOverviewTabMetadataQuery,
  useCostCodeOverviewTabQuery,
  useSaveCostCodesMutation,
} from "src/routes/admin/costCodes/CostCodeOverviewTab.generated";
import { BoundOptionPricingConfigFields } from "../components/BoundOptionPricingConfigFields";
import { maybeOptionPricingConfig, optionPricingConfigConfig } from "../utils";

type CostCodeOverviewTabProps = { costCodeId: string; isOptionCatalogV2Enabled?: boolean };

export function CostCodeOverviewTab({ costCodeId, isOptionCatalogV2Enabled }: CostCodeOverviewTabProps) {
  const query = useCostCodeOverviewTabQuery({ variables: { filter: { id: [costCodeId], version: [1, 2] } } });
  const { data } = useCostCodeOverviewTabMetadataQuery();
  const { costDivisions = [], enumDetails } = data || {};
  const [saveCostCodes] = useSaveCostCodesMutation();
  const costCode = query.data?.costCodes[0];

  const formState = useFormState({
    config: formConfig,
    init: {
      input: costCode,
      map: (cc) => ({
        id: cc.id,
        name: cc.name,
        number: cc.number,
        costClassification: cc.costClassification?.code,
        costSubClassification: cc.costSubClassification?.code,
        invoiceCategory: cc.invoiceCategory?.code,
        billType: cc.billType?.code,
        divisionId: cc.division?.id,
        intacctApAccountId: cc.intacctApAccountId,
        changeEventAutoAcceptThresholdInCents: cc.changeEventAutoAcceptThresholdInCents,
        excludeFromPurchaseOrders: cc.excludeFromPurchaseOrders,
        isPriceOnly: cc.isPriceOnly,
        finishScheduleOrder: cc.finishScheduleOrder,
        optionPricingConfig: cc.optionPricingConfig ?? {},
      }),
    },
    autoSave: async ({ changedValue, divisionId }) => {
      const { optionPricingConfig, ...others } = changedValue;
      if (formState.canSave()) {
        await saveCostCodes({
          variables: {
            input: {
              costCodes: [{ id: costCode?.id, ...others, ...maybeOptionPricingConfig({ ...optionPricingConfig }) }],
            },
          },
          // Manually update the cache to update the list table immediately whenever we change a cost division
          update(cache, result) {
            if (!changedValue.divisionId) return;
            cache.modify({
              id: cache.identify({ __typename: "CostCode", id: changedValue.id }),
              fields: {
                division() {
                  return changedValue.divisionId ?? divisionId ?? null;
                },
              },
            });
          },
        });
      }
    },
  });

  return (
    <div>
      <h2 css={Css.mb3.md.$}>Details</h2>
      <FormLines>
        <BoundSelectField
          label="Division"
          field={formState.divisionId}
          options={costDivisions}
          getOptionLabel={({ name, version }) => `${name} V${version}`}
        />
        <BoundSelectField
          label="Cost Classification"
          field={formState.costClassification}
          options={enumDetails?.costClassificationType ?? []}
          getOptionLabel={({ name }) => name}
          getOptionValue={({ code }) => code}
        />
        <BoundSelectField
          label="Cost Sub Classification"
          field={formState.costSubClassification}
          options={enumDetails?.costSubClassificationType ?? []}
          getOptionLabel={({ name }) => name}
          getOptionValue={({ code }) => code}
        />
        <BoundSelectField
          label="Invoice Category"
          field={formState.invoiceCategory}
          options={enumDetails?.costCodeInvoiceCategory ?? []}
          getOptionLabel={({ name }) => name}
          getOptionValue={({ code }) => code}
        />
        <BoundSelectField
          label="Bill Type"
          field={formState.billType}
          options={enumDetails?.billType ?? []}
          getOptionLabel={({ name }) => name}
          getOptionValue={({ code }) => code}
        />
        <BoundTextField label="Number" field={formState.number} />
        <BoundTextField label="Intacct Ap Account Id" field={formState.intacctApAccountId} />
        <BoundNumberField label="Finish Schedule Order" field={formState.finishScheduleOrder} />
        <BoundNumberField
          label="Threshold to Auto-approve Change Events Generated by Bills in the Review Log"
          type="cents"
          field={formState.changeEventAutoAcceptThresholdInCents}
        />
        {isOptionCatalogV2Enabled && (
          <BoundOptionPricingConfigFields
            postCutoffDesiredMarginInBasisPoints={formState.optionPricingConfig.postCutoffDesiredMarginInBasisPoints}
            preCutoffDesiredMarginInBasisPoints={formState.optionPricingConfig.preCutoffDesiredMarginInBasisPoints}
            selectionFeeInCents={formState.optionPricingConfig.selectionFeeInCents}
            minimumPriceInCents={formState.optionPricingConfig.minimumPriceInCents}
            reasonNote={formState.optionPricingConfig.reasonNote}
          />
        )}
        <div css={Css.addIn("& > label", Css.maxw100.$).$}>
          <BoundCheckboxField
            label="Exclude From Purchase Orders"
            description="Not available in Cost Code list of values when creating Purchase Orders."
            helperText="Except for HBL projects"
            field={formState.excludeFromPurchaseOrders}
          />
        </div>
        <div css={Css.addIn("& > label", Css.maxw100.$).$}>
          <BoundCheckboxField
            label="Price Only?"
            description="Mark this option when the cost code is used for pricing only (e.g. OH&P)."
            field={formState.isPriceOnly}
          />
        </div>
      </FormLines>
    </div>
  );
}

const formConfig: ObjectConfig<SaveCostCodeInput> = {
  id: { type: "value", rules: [required] },
  name: { type: "value" },
  number: { type: "value" },
  costClassification: { type: "value" },
  costSubClassification: { type: "value" },
  invoiceCategory: { type: "value" },
  billType: { type: "value" },
  divisionId: { type: "value" },
  intacctApAccountId: { type: "value" },
  finishScheduleOrder: { type: "value" },
  changeEventAutoAcceptThresholdInCents: { type: "value" },
  excludeFromPurchaseOrders: { type: "value" },
  isPriceOnly: { type: "value" },
  optionPricingConfig: { type: "object", config: optionPricingConfigConfig },
};
